import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export interface TutorialStep {
  selector: string;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export interface Tutorial {
  id: string;
  steps: TutorialStep[];
  completed: boolean;
}

export const useTutorialStore = defineStore('tutorial',
  () => {
    const tutorials = ref<Record<string, Tutorial>>({});
    const activeTutorial = ref<string | null>(null);
    const currentStepIndex = ref(0);

    const currentTutorial = computed(() => (activeTutorial.value ? tutorials.value[activeTutorial.value] : null));

    const currentStep = computed(() => (currentTutorial.value ? currentTutorial.value.steps[currentStepIndex.value] : null));

    const startTutorial = (tutorialId: string) => {
      if (!tutorials.value[tutorialId]) return;
      if (tutorials.value[tutorialId].completed) return;

      activeTutorial.value = tutorialId;
      currentStepIndex.value = 0;
    };

    const nextStep = () => {
      if (!currentTutorial.value) return;

      if (currentStepIndex.value < currentTutorial.value.steps.length - 1) {
        currentStepIndex.value++;
      }
      else {
        completeTutorial();
      }
    };

    const previousStep = () => {
      if (currentStepIndex.value > 0) {
        currentStepIndex.value--;
      }
    };

    const completeTutorial = () => {
      if (!activeTutorial.value || !tutorials.value[activeTutorial.value]) return;

      tutorials.value[activeTutorial.value].completed = true;
      activeTutorial.value = null;
      currentStepIndex.value = 0;
    };

    const skipTutorial = () => {
      completeTutorial();
    };

    const registerTutorial = (tutorial: Omit<Tutorial, 'completed'>) => {
      if (tutorials.value[tutorial.id]) return;

      tutorials.value[tutorial.id] = {
        ...tutorial,
        completed: false
      };
    };

    return {
      tutorials,
      activeTutorial,
      currentStep,
      currentStepIndex,
      startTutorial,
      nextStep,
      previousStep,
      skipTutorial,
      registerTutorial
    };
  },
  { persist: false });
